import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { Grid, CircularProgress, MenuItem } from '@mui/material';

import ConsultantActivesAndInactives from '@/components/molecules/modals/ConsultantActivesAndInactives';
import { isFeatureEnabled } from '@/constants/featureFlags';
import ConsultantCard from '@/pages/Common/EnterpriseDetails/EnterprisePage/components/ConsultantCard';
import ConsultantDateFilter from '@/pages/Common/EnterpriseDetails/EnterprisePage/components/ConsultantDateFilter';
import ConsultantProgressData from '@/pages/Common/EnterpriseDetails/EnterprisePage/components/ConsultantProgressData';
import ConsultantTable from '@/pages/Common/EnterpriseDetails/EnterprisePage/components/ConsultantTable';
import useDashboardAccessesService from '@/services/dashboard-accesses';
import useManagerPanelService from '@/services/managerPanel';
import { handleDownloadData } from '@/utils/helpers/downloads';
import Bar from '@components/BI/graphicBar';
import LoadingComponent from '@components/templates/LoadingComponent';

import { Section } from '../../../styles';
import { PaperContainer, Div, ContainerYears, PaperActiveAndInactive } from './styles';

const monthTranslations = {
    January: 'Janeiro',
    February: 'Fevereiro',
    March: 'Março',
    April: 'Abril',
    May: 'Maio',
    June: 'Junho',
    July: 'Julho',
    August: 'Agosto',
    September: 'Setembro',
    October: 'Outubro',
    November: 'Novembro',
    December: 'Dezembro'
};

const allMonths = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
];
const Accesses = () => {
    const [loading, setLoading] = useState(true);
    const [loadingConsultantData, setLoadingConsultantData] = useState(false);
    const [graphBarData, setGraphBarData] = useState([]);
    const [filterYear, setFilterYear] = useState(new Date().getFullYear());
    const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
    const [years, setYears] = useState([new Date().getFullYear()]);
    const [consolidatedTotal, setConsolidatedTotal] = useState(0);
    const [totalAccessesPerYear, setTotalAccessesPerYear] = useState(0);
    const [accessActivesAndInactives, setAccessActivesAndInactives] = useState(0);

    const [engagementGraphicData, setEngagementGraphicData] = useState([]);
    const [totalConsultant, setTotalConsultant] = useState(0);
    const [engagementMetricsData, setEngagementMetricsData] = useState([]);

    const [showModal, setShowModal] = useState({ isOpen: false, data: [], title: '' });

    const { getAccessLoginPDFReport } = useManagerPanelService();
    const { clickedReportButton } = useSelector((state) => state.managerPanelReducer);

    const dispatch = useDispatch();

    const { id } = useParams();
    const segment = useSelector((state) => state.auth.currentSegment);
    const enterpriseId = id;

    const { getDashboardAccesses, getDashboardAtivesAndInactives, getEngagementGraphicData, getEngagementMetrics } =
        useDashboardAccessesService();
    const selectRef = useRef();
    const options = {
        chartArea: { width: '80%', height: '80%' },
        colors: ['#62C054'],
        explorer: { axis: 'horizontal', keepInBounds: false },
        orientation: 'horizontal',
        legend: { position: 'none' },
        fontSize: 12,
        tooltip: {
            ignoreBounds: false,
            textStyle: {
                bold: true
            }
        },
        annotations: {
            textStyle: {
                fontSize: 15,
                bold: true,
                color: '#000'
            }
        },
        vAxis: {
            title: 'Quant. de acessos',
            titleTextStyle: {
                italic: false,
                color: '#9BA1A9'
            }
        }
    };

    function handleGraphBarData(data) {
        if (!data || !Object.values(data).length) {
            return;
        }

        const formattedData = [['Mês', 'Quantidade', { role: 'annotation' }]];

        allMonths.forEach((month) => {
            const translatedMonth = monthTranslations[month];
            const quantity = data[month] ? parseInt(data[month], 10) : 0;
            const annotation = quantity;
            formattedData.push([translatedMonth, quantity, annotation]);
        });

        setGraphBarData(formattedData);
    }

    const handleReport = async () => {
        try {
            dispatch({ type: 'MANAGER_PANEL_REPORT_IS_LOADING', payload: true });
            const { data } = await getAccessLoginPDFReport(enterpriseId, filterYear);

            if (data) {
                handleDownloadData(data, `relatorio_de_acessos_${filterYear}`);
            }
        } catch (error) {
            console.log(error);
        } finally {
            dispatch({ type: 'MANAGER_PANEL_RESET' });
        }
    };

    const handleData = async (filter = '') => {
        try {
            const { data } = await getDashboardAccesses(filterYear, filterMonth, enterpriseId);

            const responseAtivesAndInactives = await getDashboardAtivesAndInactives(enterpriseId);
            setAccessActivesAndInactives(responseAtivesAndInactives.data);

            const currentYear = new Date().getFullYear();
            const yearsFromApi = data?.years?.length ? data.years.map(Number) : [];
            const uniqueYears = [...new Set([...yearsFromApi, currentYear])].sort((a, b) => b - a);
            setYears(uniqueYears);

            setTotalAccessesPerYear(data.totalAccessesPerYear);
            setConsolidatedTotal(data.consolidatedTotal);
            handleGraphBarData(data.quantityPerMonth);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const handleEngagementData = async () => {
        setLoadingConsultantData(true);
        try {
            const { data } = await getEngagementGraphicData(filterYear, filterMonth == 'Todos' ? null : filterMonth, enterpriseId);
            setEngagementGraphicData(data);
            setTotalConsultant(data.totalConsultant);

            const { data: engagementMetricsData } = await getEngagementMetrics(
                filterYear,
                filterMonth == 'Todos' ? null : filterMonth,
                enterpriseId
            );
            setEngagementMetricsData(engagementMetricsData);
        } catch (error) {
            console.log(error);
        } finally {
            setLoadingConsultantData(false);
        }
    };

    useEffect(() => {
        handleData();
        handleEngagementData();
    }, []);

    useEffect(() => {
        if (clickedReportButton) {
            handleReport();
        }
    }, [clickedReportButton]);

    return (
        <>
            <Section>
                {!loading ? (
                    <>
                        <Grid container>
                            <ConsultantDateFilter
                                years={years}
                                filterYear={filterYear}
                                setFilterYear={setFilterYear}
                                filterMonth={filterMonth}
                                setFilterMonth={setFilterMonth}
                                handleFilterClick={() => {
                                    handleData();
                                    handleEngagementData();
                                }}
                                handleClean={() => {
                                    setFilterYear('');
                                    setFilterMonth('');
                                }}
                            />

                            <Grid container spacing={3} marginBottom="2rem">
                                <Grid item xs={3}>
                                    <ConsultantCard title="Total de Acessos" value={consolidatedTotal} />
                                </Grid>
                                <Grid item xs={3}>
                                    <PaperActiveAndInactive
                                        elevation={0}
                                        height="197px"
                                        onClick={() =>
                                            setShowModal({
                                                isOpen: true,
                                                data: accessActivesAndInactives.actives,
                                                title: 'Consultores ativos'
                                            })
                                        }
                                    >
                                        <div>
                                            <h2>Consultores ativos</h2>
                                            <p>(acessou nos últimos 5 dias)</p>
                                            <h1 style={{ color: '#62c054' }}>{accessActivesAndInactives.actives.length}</h1>
                                        </div>
                                    </PaperActiveAndInactive>
                                </Grid>
                                <Grid item xs={3}>
                                    <PaperActiveAndInactive
                                        height="197px"
                                        elevation={0}
                                        onClick={() =>
                                            setShowModal({
                                                isOpen: true,
                                                data: accessActivesAndInactives.inactives,
                                                title: 'Consultores inativos'
                                            })
                                        }
                                    >
                                        <div>
                                            <h2>Consultores inativos</h2>
                                            <p>(não acessou nos últimos 5 dias)</p>
                                            <h1 style={{ color: '#D35E4B' }}>{accessActivesAndInactives.inactives.length}</h1>
                                        </div>
                                    </PaperActiveAndInactive>
                                </Grid>
                                <Grid item xs={3}>
                                    <ConsultantCard title="Consultores Cadastrados" value={engagementGraphicData?.consultant ?? 0} />
                                </Grid>
                            </Grid>
                            <Grid spacing={3} xs={12} marginBottom="2rem">
                                <PaperContainer height="478px">
                                    <ContainerYears>
                                        <Div width="100%">
                                            <h1>Acessos</h1>
                                            <p>
                                                <b>{filterYear} |</b> {totalAccessesPerYear} acessos de Consultores
                                            </p>
                                        </Div>
                                    </ContainerYears>
                                    <Bar height="100%" width="100%" allData={graphBarData} options={options} />
                                </PaperContainer>
                            </Grid>
                            {loadingConsultantData ? (
                                <Grid container sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <CircularProgress />
                                </Grid>
                            ) : (
                                <>
                                    <PaperContainer height="auto">
                                        <Grid container xs={12}>
                                            <Grid item xs={12}>
                                                <ConsultantProgressData
                                                    title="Completaram o Cadastro"
                                                    value={engagementGraphicData?.allConsultant ?? 0}
                                                    maxValue={totalConsultant}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <ConsultantProgressData
                                                    title="Completaram o Check Agro"
                                                    value={engagementGraphicData?.consultantWithSectorEvaluations ?? 0}
                                                    maxValue={engagementGraphicData?.allConsultant ?? 0}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <ConsultantProgressData
                                                    title="Completaram o Check Agro por Indicador"
                                                    value={engagementGraphicData?.consultantWithIndicatorEvaluations ?? 0}
                                                    maxValue={engagementGraphicData?.allConsultant ?? 0}
                                                />
                                            </Grid>
                                            {isFeatureEnabled('DADOS_TELA_DE_ENGAJAMENTO', segment) && (
                                                <>
                                                    <Grid item xs={12}>
                                                        <ConsultantProgressData
                                                            title="Realizaram um Plano de Ação"
                                                            value={engagementGraphicData?.consultantWithActionPlans ?? 0}
                                                            maxValue={engagementGraphicData?.allConsultant ?? 0}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <ConsultantProgressData
                                                            title="Utilizaram o Simulador Econômico"
                                                            value={engagementGraphicData?.consultantWithSimulators ?? 0}
                                                            maxValue={engagementGraphicData?.allConsultant ?? 0}
                                                        />
                                                    </Grid>
                                                </>
                                            )}
                                        </Grid>
                                    </PaperContainer>

                                    <Grid container spacing={3} marginTop="1rem">
                                        {isFeatureEnabled('DADOS_TELA_DE_ENGAJAMENTO', segment) ? (
                                            <>
                                                <Grid item xs={6}>
                                                    <ConsultantCard
                                                        title="Cadastro à Propriedade"
                                                        description="(Tempo médio entre a finalização do cadastro de usuário até o cadastro da primeira Propriedade)"
                                                        value={engagementMetricsData?.avgTimeToFirstProperty ?? 'Nenhum cadastro'}
                                                        color="#8DC0BA"
                                                    />
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <ConsultantCard
                                                        title="Cadastro ao Check Agro"
                                                        description="(Tempo médio entre a finalização do cadastro de usuário até a primeira execução do Check Agro)"
                                                        value={engagementMetricsData?.avgTimeToFirstEvaluation ?? 'Nenhum cadastro'}
                                                        color="#8DC0BA"
                                                    />
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <ConsultantCard
                                                        title="Total de Planos de Ação criados"
                                                        value={engagementMetricsData?.totalActionPlans ?? 0}
                                                    />
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <ConsultantCard
                                                        title="Total de Check Agro criados"
                                                        value={engagementMetricsData?.totalEvaluations ?? 0}
                                                    />
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <ConsultantCard
                                                        title="Total de Simulações Econômicas realizadas"
                                                        value={engagementMetricsData?.totalSimulators ?? 0}
                                                    />
                                                </Grid>
                                            </>
                                        ) : (
                                            <>
                                                <Grid item xs={4}>
                                                    <ConsultantCard
                                                        title="Cadastro à Propriedade"
                                                        description="(Tempo médio entre a finalização do cadastro de usuário até o cadastro da primeira Propriedade)"
                                                        value={engagementMetricsData?.avgTimeToFirstProperty ?? 'Nenhum cadastro'}
                                                        color="#8DC0BA"
                                                    />
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <ConsultantCard
                                                        title="Total de Check Agro criados"
                                                        value={engagementMetricsData?.totalEvaluations ?? 0}
                                                    />
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <ConsultantCard
                                                        title="Cadastro ao Check Agro"
                                                        description="(Tempo médio entre a finalização do cadastro de usuário até a primeira execução do Check Agro)"
                                                        value={engagementMetricsData?.avgTimeToFirstEvaluation ?? 'Nenhum cadastro'}
                                                        color="#8DC0BA"
                                                    />
                                                </Grid>
                                            </>
                                        )}
                                    </Grid>
                                    <Grid container marginTop="2rem">
                                        <ConsultantTable
                                            enterpriseId={enterpriseId}
                                            segment={segment}
                                            filterYear={filterYear}
                                            filterMonth={filterMonth == 'Todos' ? null : filterMonth}
                                        />
                                    </Grid>
                                </>
                            )}
                        </Grid>
                        <ConsultantActivesAndInactives
                            title={showModal.title}
                            data={showModal.data}
                            isOpen={showModal.isOpen}
                            handleClose={() => setShowModal({ isOpen: false, data: [], title: '' })}
                        />
                    </>
                ) : (
                    <LoadingComponent hideLogo />
                )}
            </Section>
        </>
    );
};

export default Accesses;
